/**
 * Image Viewer System
 * Handles full-screen image viewing with zoom, pan, and metadata
 */
export default class Viewer {
	constructor() {
		this.viewer = document.getElementById('viewer');
		if(!this.viewer) {
			console.warn('Viewer element not found');
			return;
		}

		// Cache DOM elements
		this.imageContainer = this.viewer.querySelector('.viewer__image-container');
		this.image = this.viewer.querySelector('.viewer__image');
		this.loading = this.viewer.querySelector('.viewer__loading');
		this.details = this.viewer.querySelector('.viewer__details');
		this.detailsContent = this.viewer.querySelector('.viewer__details-content');
		this.zoomLevel = this.viewer.querySelector('.viewer__zoom-level');

		// Controls
		this.zoomInBtn = this.viewer.querySelector('.viewer__control--zoom-in');
		this.zoomOutBtn = this.viewer.querySelector('.viewer__control--zoom-out');
		this.detailsBtn = this.viewer.querySelector('.viewer__control--details');
		this.closeBtn = this.viewer.querySelector('.viewer__control--close');

		// Verify required elements
		if(!this.imageContainer || !this.image || !this.loading) {
			console.error('Viewer missing required elements');
			return;
		}

		// State
		this.isOpen = false;
		this.currentId = null;
		this.currentData = null;
		this.scale = 1;
		this.minScale = 1;
		this.maxScale = 1;
		this.translateX = 0;
		this.translateY = 0;
		this.isDragging = false;
		this.startX = 0;
		this.startY = 0;
		this.nativeWidth = 0;
		this.nativeHeight = 0;

		// Touch state
		this.initialDistance = 0;
		this.initialScale = 1;

		// Debounce timer for resize
		this.resizeTimeout = null;

		// Expose instance globally for URL param coordinator
		window.viewerInstance = this;

		this.init();
	}

	init() {
		// Click handlers for viewer triggers
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('[data-viewer-id]');
			if(!trigger) return;

			e.preventDefault();
			const id = trigger.dataset.viewerId;
			if(id) {
				this.open(id);
			}
		});

		// Control handlers (with null checks)
		if(this.zoomInBtn) {
			this.zoomInBtn.addEventListener('click', () => this.zoomIn());
		}
		if(this.zoomOutBtn) {
			this.zoomOutBtn.addEventListener('click', () => this.zoomOut());
		}
		if(this.detailsBtn) {
			this.detailsBtn.addEventListener('click', () => this.toggleDetails());
		}
		if(this.closeBtn) {
			this.closeBtn.addEventListener('click', () => this.close());
		}

		// Keyboard
		document.addEventListener('keydown', (e) => {
			if(!this.isOpen) return;

			switch(e.key) {
				case 'Escape':
					this.close();
					break;
				case '+':
				case '=':
					e.preventDefault();
					this.zoomIn();
					break;
				case '-':
				case '_':
					e.preventDefault();
					this.zoomOut();
					break;
			}
		});

		// Pan handlers
		this.imageContainer.addEventListener('mousedown', (e) => this.startPan(e));
		this.imageContainer.addEventListener('mousemove', (e) => this.panMove(e));
		this.imageContainer.addEventListener('mouseup', () => this.endPan());
		this.imageContainer.addEventListener('mouseleave', () => this.endPan());

		// Touch handlers for pinch zoom
		this.imageContainer.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
		this.imageContainer.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
		this.imageContainer.addEventListener('touchend', () => this.handleTouchEnd());

		// Handle window resize
		window.addEventListener('resize', () => {
			if(!this.isOpen || !this.currentData) return;

			clearTimeout(this.resizeTimeout);
			this.resizeTimeout = setTimeout(() => {
				this.calculateZoomLimits(this.currentData.width, this.currentData.height);
				this.constrainPan();
				this.updateTransform();
			}, 150);
		});

		// Browser history
		window.addEventListener('popstate', (e) => {
			if(e.state?.viewerId) {
				this.open(e.state.viewerId, false);
			} else if(this.isOpen) {
				// Restore title from the state we're navigating to
				if(e.state?.originalTitle) {
					document.title = e.state.originalTitle;
				}
				this.close(false);
			}
		});

		// Listen for modal opens to close viewer
		document.addEventListener('click', (e) => {
			if(!this.isOpen) return;

			const modalTrigger = e.target.closest('.modal-trigger, [data-modal-ajax]');
			if(modalTrigger) {
				this.close(true);
			}
		});
	}

	async open(id, updateHistory = true) {
		if(!id) {
			console.warn('Viewer.open() called without ID');
			return;
		}

		this.currentId = id;
		this.isOpen = true;

		// Show viewer as modal (adds to top layer)
		// Viewer will appear on top of any existing modals due to dialog stacking order
		try {
			this.viewer.showModal();
		} catch(error) {
			console.error('Error opening viewer dialog:', error);
			this.isOpen = false;
			return;
		}

		// Add active class for transitions
		void this.viewer.offsetHeight; // Force reflow
		this.viewer.classList.add('is-active');

		this.loading.style.display = '';
		this.imageContainer.style.display = 'none';

		if(this.details) {
			this.details.classList.remove('is-visible');
		}
		if(this.detailsBtn) {
			this.detailsBtn.classList.remove('is-active');
		}

		// Update URL
		if(updateHistory) {
			const url = new URL(window.location);
			url.searchParams.set('sk8nigs-viewer', id);

			// Preserve originalTitle from previous state, or capture current title
			const originalTitle = history.state?.originalTitle || document.title;

			try {
				history.pushState({
					viewerId: id,
					originalTitle: originalTitle
				}, '', url);
			} catch(error) {
				console.error('Error pushing viewer state:', error);
			}
		}

		// Fetch data
		try {
			const response = await fetch(`/wp-json/sk8nigs/v1/viewer/${id}`);

			if(!response.ok) {
				throw new Error(`HTTP ${response.status}: Failed to fetch viewer data`);
			}

			const data = await response.json();

			if(!data.url) {
				throw new Error('Invalid viewer data: missing image URL');
			}

			this.currentData = data;

			// Update document title
			if(data.title) {
				document.title = data.title;
			} else if(data.alt) {
				document.title = data.alt;
			}

			// Populate details HTML from server
			if(this.detailsContent && data.html) {
				this.detailsContent.innerHTML = data.html;
			}

			// Load image
			await this.loadImage(data);

			// Show image container, hide loading
			this.loading.style.display = 'none';
			this.imageContainer.style.display = '';

			// Force reflow to ensure container has dimensions
			void this.imageContainer.offsetHeight;

			// NOW calculate zoom with actual container dimensions
			this.calculateZoomLimits(data.width || this.nativeWidth, data.height || this.nativeHeight);
			this.resetTransform();

		} catch(error) {
			console.error('Viewer error:', error);
			this.close();
		}
	}

	loadImage(data) {
		return new Promise((resolve, reject) => {
			if(!data.url) {
				reject(new Error('No image URL provided'));
				return;
			}

			const img = new Image();

			img.onload = () => {
				this.image.src = data.url;
				this.image.alt = data.alt || '';

				// Store native dimensions
				this.nativeWidth = data.width || img.naturalWidth;
				this.nativeHeight = data.height || img.naturalHeight;

				resolve();
			};

			img.onerror = () => {
				reject(new Error(`Failed to load image: ${data.url}`));
			};

			img.src = data.url;
		});
	}

	calculateZoomLimits(imageWidth, imageHeight) {
		if(!imageWidth || !imageHeight) {
			console.warn('Invalid image dimensions for zoom calculation');
			return;
		}

		// Get container dimensions
		const containerWidth = this.imageContainer.clientWidth;
		const containerHeight = this.imageContainer.clientHeight;

		if(!containerWidth || !containerHeight) {
			console.warn('Invalid container dimensions for zoom calculation');
			return;
		}

		// Calculate scale to fit (object-fit: contain behavior)
		const scaleX = containerWidth / imageWidth;
		const scaleY = containerHeight / imageHeight;
		this.minScale = Math.min(scaleX, scaleY);

		// Max scale is always 1.0 (100% = native image size)
		this.maxScale = 1.0;

		// Start at fitted scale
		this.scale = this.minScale;

		this.updateZoomControls();
	}

	resetTransform() {
		this.scale = this.minScale;
		this.translateX = 0;
		this.translateY = 0;
		this.updateTransform();
	}

	updateTransform() {
		// Apply scale transform only
		this.image.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;

		// Display zoom as percentage of NATIVE size
		if(this.zoomLevel) {
			this.zoomLevel.textContent = `${Math.round(this.scale * 100)}%`;
		}

		this.updateZoomControls();

		// Update cursor
		if(this.scale > this.minScale) {
			this.imageContainer.style.cursor = this.isDragging ? 'grabbing' : 'grab';
		} else {
			this.imageContainer.style.cursor = 'default';
		}
	}

	updateZoomControls() {
		if(this.zoomOutBtn) {
			this.zoomOutBtn.disabled = this.scale <= this.minScale;
		}
		if(this.zoomInBtn) {
			this.zoomInBtn.disabled = this.scale >= this.maxScale;
		}
	}

	zoomIn() {
		const step = 0.25;
		const newScale = Math.min(this.scale + step, this.maxScale);
		this.setZoom(newScale);
	}

	zoomOut() {
		const step = 0.25;
		const newScale = Math.max(this.scale - step, this.minScale);
		this.setZoom(newScale);
	}

	setZoom(newScale) {
		const oldScale = this.scale;
		this.scale = newScale;

		// Reset pan when zooming out to min
		if(this.scale === this.minScale) {
			this.translateX = 0;
			this.translateY = 0;
		} else if(oldScale !== 0) {
			// Adjust pan to keep image centered during zoom
			const scaleRatio = this.scale / oldScale;
			this.translateX *= scaleRatio;
			this.translateY *= scaleRatio;
			this.constrainPan();
		}

		this.updateTransform();
	}

	startPan(e) {
		if(this.scale <= this.minScale) return;

		this.isDragging = true;
		this.startX = e.clientX - this.translateX;
		this.startY = e.clientY - this.translateY;
		this.imageContainer.style.cursor = 'grabbing';
		e.preventDefault();
	}

	panMove(e) {
		if(!this.isDragging) return;

		this.translateX = e.clientX - this.startX;
		this.translateY = e.clientY - this.startY;

		this.constrainPan();
		this.updateTransform();
	}

	endPan() {
		this.isDragging = false;
		if(this.scale > this.minScale) {
			this.imageContainer.style.cursor = 'grab';
		}
	}

	constrainPan() {
		if(!this.currentData) return;

		const containerWidth = this.imageContainer.clientWidth;
		const containerHeight = this.imageContainer.clientHeight;
		const imageWidth = (this.currentData.width || this.nativeWidth) * this.scale;
		const imageHeight = (this.currentData.height || this.nativeHeight) * this.scale;

		// Only constrain if image is larger than container
		if(imageWidth > containerWidth) {
			const maxX = (imageWidth - containerWidth) / 2;
			this.translateX = Math.max(-maxX, Math.min(maxX, this.translateX));
		} else {
			this.translateX = 0;
		}

		if(imageHeight > containerHeight) {
			const maxY = (imageHeight - containerHeight) / 2;
			this.translateY = Math.max(-maxY, Math.min(maxY, this.translateY));
		} else {
			this.translateY = 0;
		}
	}

	handleTouchStart(e) {
		if(e.touches.length === 2) {
			// Pinch zoom starting
			e.preventDefault();
			this.initialDistance = this.getDistance(e.touches[0], e.touches[1]);
			this.initialScale = this.scale;
		} else if(e.touches.length === 1 && this.scale > this.minScale) {
			// Pan starting
			this.isDragging = true;
			this.startX = e.touches[0].clientX - this.translateX;
			this.startY = e.touches[0].clientY - this.translateY;
		}
	}

	handleTouchMove(e) {
		if(e.touches.length === 2) {
			// Pinch zoom
			e.preventDefault();
			const currentDistance = this.getDistance(e.touches[0], e.touches[1]);
			const scaleChange = currentDistance / this.initialDistance;
			const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.initialScale * scaleChange));
			this.setZoom(newScale);
		} else if(e.touches.length === 1 && this.isDragging) {
			// Pan
			e.preventDefault();
			this.translateX = e.touches[0].clientX - this.startX;
			this.translateY = e.touches[0].clientY - this.startY;
			this.constrainPan();
			this.updateTransform();
		}
	}

	handleTouchEnd() {
		this.isDragging = false;
		this.initialDistance = 0;
	}

	getDistance(touch1, touch2) {
		const dx = touch1.clientX - touch2.clientX;
		const dy = touch1.clientY - touch2.clientY;
		return Math.sqrt(dx * dx + dy * dy);
	}

	toggleDetails() {
		if(!this.details) return;

		const isVisible = this.details.classList.toggle('is-visible');
		this.details.setAttribute('aria-hidden', String(!isVisible));

		// Toggle active state on button
		if(this.detailsBtn) {
			this.detailsBtn.classList.toggle('is-active', isVisible);
		}
	}

	close(updateHistory = true) {
		if(!this.isOpen) return;

		this.isOpen = false;

		// Restore original title if we have it in state
		if(history.state?.originalTitle) {
			document.title = history.state.originalTitle;
		}

		this.viewer.classList.remove('is-active');

		// Close dialog after transition
		setTimeout(() => {
			try {
				this.viewer.close();
			} catch(error) {
				console.error('Error closing viewer:', error);
			}
		}, 300); // Match your CSS transition duration

		if(this.details) {
			this.details.classList.remove('is-visible');
		}
		if(this.detailsBtn) {
			this.detailsBtn.classList.remove('is-active');
		}

		this.currentId = null;
		this.currentData = null;

		if(updateHistory) {
			const url = new URL(window.location);
			url.searchParams.delete('sk8nigs-viewer');

			// Push new state WITHOUT originalTitle - we're back at the base page
			try {
				history.pushState({}, '', url);
			} catch(error) {
				console.error('Error updating history on close:', error);
			}
		}
	}
}